import { Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ChangeDetectionStrategy, Component, OnDestroy, ViewChild } from '@angular/core';

import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnDataListComponent } from '@lib/ui/data-list';

type StatusKey = 'awaitingDecision' | 'active' | 'rejected';
type ConditionsKey = 'fullSharing' | 'alertsOnly';

interface TableRow {
  id: string;
  agency: string;
  state: string;
  status: StatusKey;
  lastUpdate: string;
  conditions: ConditionsKey;
}

@Component({
  selector: 'fs-lpr-incoming-table',
  templateUrl: './incoming-table.component.html',
  styleUrls: ['./incoming-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IncomingTableComponent implements OnDestroy {
  @ViewChild('list')
  public readonly list: SnDataListComponent<TableRow>;

  public readonly loadData = this._loadData.bind(this);
  public readonly columns = ['agency', 'state', 'status', 'lastUpdate', 'conditions', 'actions'];
  
  readonly statusTranslationKeys: Record<StatusKey, string> = {
    awaitingDecision: 'vehicleIntelligence.lprSharing.statuses.awaitingDecision',
    active: 'vehicleIntelligence.lprSharing.statuses.active',
    rejected: 'vehicleIntelligence.lprSharing.statuses.rejected',
  };

  readonly conditionTranslationKeys: Record<ConditionsKey, string> = {
    fullSharing: 'vehicleIntelligence.lprSharing.conditionsMap.fullSharing',
    alertsOnly: 'vehicleIntelligence.lprSharing.conditionsMap.alertsOnly',
  };

  private readonly _destroy$ = new Subject<void>();

  private readonly _mockData: TableRow[] = Array.from({ length: 25 }, (_, i) => ({
    id: `incoming-${i}`,
    agency: `Agency ${String.fromCharCode(65 + (i % 26))}`,
    state: ['NY', 'FL', 'TX', 'CA'][i % 4],
    status: (['awaitingDecision', 'active', 'rejected'] as StatusKey[])[i % 3],
    lastUpdate: `11/${(i % 28) + 1}/2024`,
    conditions: (i % 2 === 0 ? 'fullSharing' : 'alertsOnly') as ConditionsKey,
  }));

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _loadData(params?: ISnListQueryParameters): Observable<ISnListResponse<TableRow>> {
    const pageSize = params?.size || 20;
    const page = params?.page || 1;
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    // Simulate pagination
    const paginatedData = this._mockData.slice(startIndex, endIndex);
    const totalPages = Math.ceil(this._mockData.length / pageSize);

    return of({
      results: paginatedData,
      totalPages,
      count: this._mockData.length,
    }).pipe(
      takeUntil(this._destroy$)
    );
  }
}
